import { HttpError } from 'react-admin';
import {
  getVariantSkuDetails,
  updateVariantSkuDetails,
} from '@src/apis/reware/catalog';
import { requireAuth } from '../shared/auth';
import { requirePartner } from '../shared/partner';
import type {
  VariantSkuDetails,
  UpdateVariantSkuDetailsArgs,
  VariantSkusDataProvider,
} from '@src/types/catalog';

/**
 * Data provider for variant SKU management
 * Handles getOne and update operations for individual variant SKUs
 */
export const variantSkusDataProvider: VariantSkusDataProvider = {
  /**
   * Fetch a single variant SKU by its SKU identifier
   * Note: The SKU needs to be passed in as id from React Admin
   * Also expects parentSku in the meta parameter
   */
  getOne: async (resource, params) => {
    try {
      const token = requireAuth();
      const currentPartner = requirePartner();
      const sku = params.id as string;

      const variantDetails = await getVariantSkuDetails({
        token,
        partnerCode: currentPartner.code,
        sku,
      });

      // Transform to React Admin format
      const data: VariantSkuDetails = {
        ...variantDetails,
        id: sku,
        sku,
        parentSku: params.meta?.parentSku || '',
      };

      return { data: data as any };
    } catch (error) {
      console.error('Error fetching variant SKU details:', error);
      const status = error.status ? error.status : 500;
      throw new HttpError('Failed to fetch variant details.', status);
    }
  },

  /**
   * Update a variant SKU
   * Supports file uploads for images
   */
  update: async (resource, params) => {
    try {
      const token = requireAuth();
      const currentPartner = requirePartner();
      const sku = params.id as string;

      const updateArgs: UpdateVariantSkuDetailsArgs = {
        token,
        partnerCode: currentPartner.code,
        sku,
        upc: params.data.upc,
        mpn: params.data.mpn,
        gtin: params.data.gtin,
        partnerSku: params.data.partnerSku,
        partnerParentSku: params.data.partnerParentSku,
        family: params.data.family,
        data: params.data.data,
        files: params.data.files,
        imageKeys: params.data.imageKeys,
      };

      const updatedVariant = await updateVariantSkuDetails(updateArgs);

      // Transform to React Admin format
      const data: VariantSkuDetails = {
        ...updatedVariant,
        id: sku,
        sku,
        parentSku: params.previousData.parentSku,
      };

      return { data: data as any };
    } catch (error) {
      console.error('Error updating variant SKU details:', error);
      const status = error.status ? error.status : 500;
      throw new HttpError('Failed to update variant details.', status);
    }
  },

  /**
   * Not implemented methods - throw errors
   */
  getList: async () => {
    throw new HttpError('getList not implemented for variant SKUs', 501);
  },

  getMany: async () => {
    throw new HttpError('getMany not implemented for variant SKUs', 501);
  },

  getManyReference: async () => {
    throw new HttpError('getManyReference not implemented for variant SKUs', 501);
  },

  create: async () => {
    throw new HttpError('create not implemented for variant SKUs', 501);
  },

  delete: async () => {
    throw new HttpError('delete not implemented for variant SKUs', 501);
  },

  deleteMany: async () => {
    throw new HttpError('deleteMany not implemented for variant SKUs', 501);
  },

  updateMany: async () => {
    throw new HttpError('updateMany not implemented for variant SKUs', 501);
  },
};
